<template>
  <div class="container">
    <PageHeader
      :title="translate('Page not found')"
      :introduction="translate('The page you are looking for does not exist or has moved.')"
    />
    <section class="not-found-content d-flex flex-column align-items-center text-center gap-3 py-5">
      <Icon icon="material-symbols:search-off-rounded" class="not-found-icon text-secondary" />
      <h2 class="h4 mb-0">{{ translate('There was an error while loading the page.') }}</h2>
      <p class="mb-0 text-secondary">
        {{ translate('Check the address, or use the menu to find what you need.') }}
      </p>
      <div class="d-flex flex-wrap justify-content-center gap-2">
        <router-link to="/" class="btn btn-primary">
          {{ translate('Return to the homepage') }}
        </router-link>
        <BootstrapButton variant="ghost" @click="feedbackOpen">
          {{ translate('Report a problem') }}
        </BootstrapButton>
      </div>
    </section>
  </div>
  <DialogDrawer
    ref="dialog"
    data-testid="translation-found-dialog"
    placement="centre"
    modal
    @close="
      oppositeLanguagePage = undefined;
      currentLanguagePage = undefined;
    "
    @submit="addRoute"
  >
    <template #title>{{ translate('Translation found') }}</template>
    <p>
      {{
        translate('This page URL was found in Dutch. Would you like to show the translated page?')
      }}
    </p>
    <template #buttons>
      <BootstrapButton
        name="cancel"
        variant="ghost"
        value="cancel"
        formmethod="dialog"
        @click="currentLanguagePage ? navigate(currentLanguagePage) : dialog?.close()"
      >
        {{ translate('Cancel') }}
      </BootstrapButton>
      <BootstrapButton
        type="submit"
        name="submit"
        variant="primary"
        value="default"
        :aria-label="translate('Show translated page')"
      >
        {{ translate('Show') }}
      </BootstrapButton>
    </template>
  </DialogDrawer>
</template>
<script setup lang="ts">
import {findTranslationOfSlug, findTranslationOfId} from '@/components/root/error/NotFound';
import DialogDrawer from '@/components/shared/custom/DialogDrawer/DialogDrawer.vue';
import {RouteRecordRaw} from 'vue-router';
import {
  feedbackOpenKey,
  FeedbackOpen,
} from '@/components/shared/custom/FeedbackSlideIn/injectionKeys';

const feedbackOpen = inject<FeedbackOpen>(feedbackOpenKey) as FeedbackOpen;

const dialog = useTemplateRef<InstanceType<typeof DialogDrawer>>('dialog');
const router = useRouter();
const {translate} = useLocalisation();
const oppositeLanguagePage = ref<SpherePage<SpherePageType>>();
const currentLanguagePage = ref<SpherePage<SpherePageType>>();

const navigate = (page: SpherePage<SpherePageType>) => {
  const prefix = page.meta.type === 'cms.DashboardPage' ? 'core' : 'page';
  const to = router.resolve({name: `${prefix}-${page.meta.id}`});
  if (to.name !== 'not-found') router.push(to); // N.B push to move on from 'not-found'
  dialog.value?.close();
};

/**
 * Instead of switching language & re-fetching all pages, why not dynamically add the route?
 * N.B /api/pages/:id will 404 in the wrong locale. Handle this is `PageView.vue`.
 * @see {@link apollo_portal/src/components/pages/PageView.vue}
 */
const addRoute = async () => {
  const meta = oppositeLanguagePage.value!.meta; // The only core we can navigate to is /home
  if (meta.type === 'cms.DashboardPage')
    throw Error('The Home route should be common to both router locales.');
  else {
    // prettier-ignore
    const node: TreeNodePage = {id: meta.id, title: meta.title, meta, expanded: false, disabled: false, children: [], node_type: 'PageNode'};
    const route: RouteRecordRaw = {
      name: `page-${node.meta.id}`,
      path: meta.url_path.slice(node.meta.url_path.indexOf('/', 1)),
      component: () => import('@/components/pages/PageView.vue'),
      props: () => ({meta}),
      meta: {node},
    };
    router.addRoute('core-dynamic', route);
    router.replace(route);
  }
};

// Find a translation for a given route when matched with `NotFoundView.vue`
onMounted(async () => {
  try {
    if (router.currentRoute.value.meta.previousRoute?.name === undefined) {
      const [opposite, current] = await findTranslationOfSlug(router.currentRoute.value);
      oppositeLanguagePage.value = opposite;
      currentLanguagePage.value = current;
      if (oppositeLanguagePage.value) dialog.value?.openModal();
    } else {
      const page = await findTranslationOfId(router.currentRoute.value.meta.previousRoute);
      if (page) navigate(page);
    }
  } catch {
    dialog.value?.close();
  }
});
</script>
<style lang="scss" scoped>
.not-found-icon {
  font-size: 4rem;
}
</style>
